import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {CurveNode} from "#template/nodes/object/curve/curve-node"
import type {ObjectNodeDefinition, ObjectNodeParameters} from "#template/nodes/object/types"
import {objectNodeParameters} from "#template/nodes/object/validation"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {CurveParametersCommon, PlanarCurveControlPoint} from "#template/nodes/object/curve/types"
import {curveParametersCommon, planarCurveControlPoint} from "#template/nodes/object/curve/validation"
import type {OnCompileContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {Matrix4, Vector3} from "@cm/math"
import {NotReady} from "#template/runtime-graph/slots"
import type {CurveData} from "#template/interfaces/object-data"
import type {InfoNodes} from "#template/interfaces/info-nodes"
import type {SceneNodes} from "#template/interfaces/scene-object"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import {completeCurveDataForControlPointsDefinedCurve} from "#template/nodes/object/curve/mesh-curve"
import {setupInfoNode} from "#template/utils/info-node-utils"
import {visitNone} from "#template/template-node/visit-helpers"

const TD = TypeDescriptors

const sceneNodeArrayDesc = TD.Array(TD.Identity<SceneNodes.SceneNode>())

export type PlanarCurveParameters = {
    name: string
    controlPoints: PlanarCurveControlPoint[]
} & CurveParametersCommon &
    ObjectNodeParameters

const planarCurveDefinition: ObjectNodeDefinition<PlanarCurveParameters> = {
    nodeClass: "PlanarCurve",
    validation: {
        parameters: z.extend(
            z.extend(
                z.object({
                    name: z.string(),
                    controlPoints: z.array(planarCurveControlPoint),
                }),
                curveParametersCommon.shape,
            ),
            objectNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class PlanarCurve extends CurveNode<PlanarCurveParameters> {
    public override get definition() {
        return planarCurveDefinition
    }

    evaluatedCurveData: CurveData | null = null

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<PlanarCurveParameters> {
        const {evaluator} = context
        const {templateScope} = evaluator

        const {controlPoints: controlPointsData, closed, resolution} = this.parameters

        const scope = evaluator.getScope(this)

        const controlPoints = scope.pureLambda(
            scope.tuple(controlPointsData),
            ([controlPointsData]) =>
                controlPointsData.map((point) => {
                    return {
                        position: Vector3.fromArray([...point.position, 0]),
                        normal: Vector3.fromArray([0, 0, 1]),
                        corner: point.corner,
                        sectionBreak: point.sectionBreak ?? false,
                    }
                }),
            "controlPoints",
        )

        const completeCurveData = completeCurveDataForControlPointsDefinedCurve(scope, controlPoints, closed, resolution)

        const curveData = scope.struct<CurveData>("CurveData", {
            type: "curve",
            visible: this.parameters.visible,
            controlPoints,
            closed,
            completeCurveData,
            matrix: Matrix4.identity(),
        })

        templateScope.alias(curveData, `curveData-${evaluator.getLocalId(this)}`)

        scope.tap(curveData, (curveData) => {
            if (curveData === NotReady) return
            this.evaluatedCurveData = curveData as CurveData
        })

        this.setupObject(scope, context, "PlanarCurve", undefined, undefined, (_objectProps) => scope.value([], sceneNodeArrayDesc, "sceneNodes"))

        setupInfoNode(this, scope, context, "PlanarCurve", (props) =>
            scope.struct<InfoNodes.PlanarCurve>("PlanarCurve", {
                type: "PlanarCurve",
                ...props,
                curveData,
            }),
        )

        return visitNone(this.parameters)
    }
}
